import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash, FaChevronDown, FaChevronRight, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import categoriesApi from '../../services/categoriesApi.js';
import categoriaGruposApi from '../../services/categoriaGruposApi.js';
import { colors } from '../../utils/colors.js';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 22px;
  color: ${colors.gray[900]};
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${colors.gradients.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  cursor: pointer;
`;

const List = styled.div`
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid ${colors.gray[100]};
  padding-left: ${(props) => 20 + props.$depth * 24}px;

  &:last-child {
    border-bottom: none;
  }
`;

const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${colors.gray[400]};
  width: 16px;
  display: flex;
  align-items: center;
`;

const Nome = styled.span`
  font-weight: 500;
  color: ${colors.gray[900]};
`;

const Badge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(props) => (props.$visibility === 'public' ? colors.success.light : colors.gray[100])};
  color: ${(props) => (props.$visibility === 'public' ? colors.success.dark : colors.gray[600])};
`;

const StatusBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${(props) => (props.$status === 'active' ? colors.success.light : colors.warning.light)};
  color: ${(props) => (props.$status === 'active' ? colors.success.dark : colors.warning.dark)};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${(props) => props.$danger ? colors.error.main : colors.gray[500]};
  font-size: 14px;
  padding: 6px;

  &:hover {
    color: ${(props) => props.$danger ? colors.error.dark : colors.primary[600]};
  }
`;

const Empty = styled.div`
  padding: 40px;
  text-align: center;
  color: ${colors.gray[500]};
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.form`
  background: ${colors.white};
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  max-width: 420px;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  margin: 0 0 20px;
  color: ${colors.gray[900]};
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.gray[700]};
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${colors.gray[300]};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${colors.gray[700]};
  margin-bottom: 20px;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: none;
`;

const CancelButton = styled(Button)`
  background: ${colors.gray[100]};
  color: ${colors.gray[700]};
`;

const SaveButton = styled(Button)`
  background: ${colors.gradients.primary};
  color: ${colors.white};
`;

const ErrorBox = styled.div`
  background: ${colors.error.light};
  color: ${colors.error.dark};
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 13px;
  margin-bottom: 16px;
`;

function flattenRoots(tree) {
  // Só categorias globais (space_id null) fazem sentido para o cadastro de spaces
  return tree.filter((cat) => !cat.space_id);
}

function CategoryRow({ category, depth, expanded, onToggle, onEdit, onDelete, onToggleStatus }) {
  const hasChildren = category.children && category.children.length > 0;
  const isExpanded = expanded.has(category.id);
  const status = category.status || 'active';

  return (
    <>
      <Row $depth={depth}>
        <RowLeft>
          {hasChildren ? (
            <ToggleButton onClick={() => onToggle(category.id)} type="button">
              {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            </ToggleButton>
          ) : (
            <span style={{ width: 16, display: 'inline-block' }} />
          )}
          <Nome>{category.nome}</Nome>
          <Badge $visibility={category.visibility}>{category.visibility}</Badge>
          <StatusBadge $status={status}>{status === 'active' ? 'ativa' : 'inativa'}</StatusBadge>
        </RowLeft>
        <Actions>
          <IconButton
            type="button"
            onClick={() => onToggleStatus(category)}
            title={status === 'active' ? 'Inativar' : 'Ativar'}
          >
            {status === 'active' ? <FaToggleOn /> : <FaToggleOff />}
          </IconButton>
          <IconButton type="button" onClick={() => onEdit(category)} title="Editar">
            <FaEdit />
          </IconButton>
          <IconButton type="button" $danger onClick={() => onDelete(category)} title="Remover">
            <FaTrash />
          </IconButton>
        </Actions>
      </Row>
      {hasChildren &&
        isExpanded &&
        category.children.map((child) => (
          <CategoryRow
            key={child.id}
            category={child}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleStatus={onToggleStatus}
          />
        ))}
    </>
  );
}

export default function CategoryManager() {
  const [roots, setRoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    nome: '',
    parent_id: '',
    visibility: 'public',
    is_personalizavel: false,
    status: 'active',
    status_search: false,
    grupo_id: '',
  });
  const [categoriaGrupos, setCategoriaGrupos] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await categoriesApi.hierarchy();
      setRoots(flattenRoots(data.categories || []));
    } catch (err) {
      // silencioso: a lista simplesmente fica vazia
    } finally {
      setLoading(false);
    }
  }, []);

  const loadGrupos = useCallback(async () => {
    try {
      // Categorias geridas aqui são todas globais (space_id null), então só
      // grupos globais fazem sentido no select.
      const data = await categoriaGruposApi.list();
      setCategoriaGrupos((data || []).filter((g) => !g.space_id));
    } catch (err) {
      // silencioso: select de grupos fica vazio
    }
  }, []);

  useEffect(() => {
    load();
    loadGrupos();
  }, [load, loadGrupos]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openCreate = () => {
    setEditing(null);
    setForm({
      nome: '',
      parent_id: '',
      visibility: 'public',
      is_personalizavel: false,
      status: 'active',
      status_search: false,
      grupo_id: '',
    });
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (category) => {
    setEditing(category);
    setForm({
      nome: category.nome,
      parent_id: category.parent_id || '',
      visibility: category.visibility,
      is_personalizavel: category.is_personalizavel,
      status: category.status || 'active',
      status_search: !!category.status_search,
      grupo_id: category.grupo_id || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Remover a categoria "${category.nome}"?`)) return;
    try {
      await categoriesApi.remove(category.id);
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível remover a categoria.');
    }
  };

  const handleToggleStatus = async (category) => {
    const novoStatus = (category.status || 'active') === 'active' ? 'inactive' : 'active';
    try {
      await categoriesApi.update(category.id, { status: novoStatus });
      await load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Não foi possível alterar o status da categoria.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    const payload = {
      nome: form.nome,
      visibility: form.visibility,
      is_personalizavel: form.is_personalizavel,
      parent_id: form.parent_id || null,
      status: form.status,
      status_search: form.status_search,
      grupo_id: form.parent_id ? null : (form.grupo_id || null),
    };
    try {
      if (editing) {
        await categoriesApi.update(editing.id, payload);
      } else {
        await categoriesApi.create(payload);
      }
      setShowModal(false);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Não foi possível salvar a categoria.');
    }
  };

  return (
    <div>
      <Header>
        <Title>Categorias e Subcategorias</Title>
        <AddButton type="button" onClick={openCreate}>
          <FaPlus /> Nova categoria
        </AddButton>
      </Header>

      <List>
        {loading && <Empty>Carregando...</Empty>}
        {!loading && roots.length === 0 && <Empty>Nenhuma categoria cadastrada ainda.</Empty>}
        {!loading &&
          roots.map((category) => (
            <CategoryRow
              key={category.id}
              category={category}
              depth={0}
              expanded={expanded}
              onToggle={toggle}
              onEdit={openEdit}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
            />
          ))}
      </List>

      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <ModalTitle>{editing ? 'Editar categoria' : 'Nova categoria'}</ModalTitle>

            {formError && <ErrorBox>{formError}</ErrorBox>}

            <Label htmlFor="nome">Nome</Label>
            <Input
              id="nome"
              value={form.nome}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              required
              autoFocus
            />

            <Label htmlFor="parent_id">Categoria pai (opcional, para criar subcategoria)</Label>
            <Select
              id="parent_id"
              value={form.parent_id}
              onChange={(e) => setForm((f) => ({ ...f, parent_id: e.target.value }))}
            >
              <option value="">Nenhuma (categoria raiz)</option>
              {roots
                .filter((r) => !editing || r.id !== editing.id)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nome}
                  </option>
                ))}
            </Select>

            <Label htmlFor="visibility">Visibilidade</Label>
            <Select
              id="visibility"
              value={form.visibility}
              onChange={(e) => setForm((f) => ({ ...f, visibility: e.target.value }))}
            >
              <option value="public">Pública</option>
              <option value="private">Privada</option>
            </Select>

            <CheckboxRow>
              <input
                type="checkbox"
                checked={form.is_personalizavel}
                onChange={(e) => setForm((f) => ({ ...f, is_personalizavel: e.target.checked }))}
              />
              Personalizável pelo dono do space
            </CheckboxRow>

            <Label htmlFor="status">Status</Label>
            <Select
              id="status"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="active">Ativa</option>
              <option value="inactive">Inativa</option>
            </Select>

            {!form.parent_id && (
              <>
                <Label htmlFor="grupo_id">Grupo (opcional, só para categorias raiz)</Label>
                <Select
                  id="grupo_id"
                  value={form.grupo_id}
                  onChange={(e) => setForm((f) => ({ ...f, grupo_id: e.target.value }))}
                >
                  <option value="">Nenhum grupo</option>
                  {categoriaGrupos.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.titulo}
                    </option>
                  ))}
                </Select>
              </>
            )}

            <CheckboxRow>
              <input
                type="checkbox"
                checked={form.status_search}
                onChange={(e) => setForm((f) => ({ ...f, status_search: e.target.checked }))}
              />
              Habilitar para busca de spaces por categoria (em breve)
            </CheckboxRow>

            <ModalActions>
              <CancelButton type="button" onClick={() => setShowModal(false)}>
                Cancelar
              </CancelButton>
              <SaveButton type="submit">Salvar</SaveButton>
            </ModalActions>
          </ModalCard>
        </ModalOverlay>
      )}
    </div>
  );
}
